import type { SourceType } from '../../../shared/api/types'

/** Матрица квоты → поле цены в позиции (request_item). */
export const PRICE_FIELD_BY_MATRIX: Record<SourceType, string> = {
  STOCK: 'priceStock',
  CHINA: 'targetPrice',
  RF: 'priceRF',
  MANUFACTURER: 'priceManufacturer',
  OTHER: 'priceOther',
}

/** Курсы валют к рублю. CNY обязателен (через него считаем не-РФ матрицы). */
export interface MatrixRates {
  USD: number
  EUR: number
  CNY: number
}

/**
 * Приводит справочник курсов (ответ /exchange-rates) к MatrixRates.
 * Возвращает null, если курсы ещё не загрузились или пришли непригодными
 * (0, отрицательные, NaN) — считать по ним нельзя.
 *
 * Фолбэк-курсов (90 / 98 / 12.5) здесь больше нет: пока справочник не
 * загружен, цена по выдуманному курсу уезжала PATCH-ом прямо в БД —
 * портились данные, а не только экран. Нет курсов — нет расчёта.
 */
export function toMatrixRates(raw: Partial<MatrixRates> | null | undefined): MatrixRates | null {
  if (!raw) return null
  const ok = (v: number | undefined): v is number =>
    typeof v === 'number' && Number.isFinite(v) && v > 0
  const { USD, EUR, CNY } = raw
  if (!ok(USD) || !ok(EUR) || !ok(CNY)) return null
  return { USD, EUR, CNY }
}

/**
 * Конвертирует цену квоты (в её валюте) в значение поля цены матрицы позиции.
 *
 * В модели CRM поле матрицы РФ хранится в рублях, а поля остальных матриц
 * (Склад/Китай/Производители/Стоки) — в юанях (CNY-эквивалент), потому что в
 * ячейке снизу показывается «N ¥». Поэтому конвертация зависит от целевой
 * матрицы:
 *   - РФ:        приводим к рублям (USD/EUR/CNY × курс, RUB как есть);
 *   - остальные: приводим к юаням (USD/EUR через кросс-курс к CNY,
 *                RUB делим на курс CNY, CNY как есть).
 *
 * Это ровно та логика, что раньше жила инлайн в QuotesTable при выборе квоты
 * чекбоксом — вынесена сюда, чтобы переиспользовать при вводе цены из ячейки
 * (модалка квоты) и в поповере-списке квот.
 *
 * `rates` обязателен: без курсов считать нечего, вызывающий код обязан сначала
 * дождаться справочника (см. toMatrixRates / useMatrixRates).
 */
export function convertQuoteToMatrixField(
  price: number,
  currency: string | null | undefined,
  sourceType: SourceType,
  rates: MatrixRates,
): number {
  const r = rates
  const cur = currency ?? 'CNY'
  const field = PRICE_FIELD_BY_MATRIX[sourceType] ?? 'targetPrice'
  let value = price

  if (field === 'priceRF') {
    if (cur === 'USD') value *= r.USD
    else if (cur === 'EUR') value *= r.EUR
    else if (cur === 'CNY') value *= r.CNY
    // RUB — уже в рублях
  } else {
    if (cur === 'USD') value *= r.USD / r.CNY
    else if (cur === 'EUR') value *= r.EUR / r.CNY
    else if (cur === 'RUB') value /= r.CNY
    // CNY — уже в юанях
  }

  return Math.round(value * 100) / 100
}
